import React, { useRef, useEffect, useState } from "react";
import { Box, Button, IconButton, Skeleton, Typography } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { ArrowLeft, ArrowRight } from "iconsax-react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

export default function Caro({ trim }) {
    const [loadedSlides, setLoadedSlides] = useState({});
    const markLoaded = (index) =>
        setLoadedSlides((prev) => (prev[index] ? prev : { ...prev, [index]: true }));
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);
    const videoRefs = useRef({});
    const [activeIndex, setActiveIndex] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    useEffect(() => {
        if (swiperRef.current && swiperRef.current.navigation) {
            setTimeout(() => {
                swiperRef.current.params.navigation.prevEl = prevRef.current;
                swiperRef.current.params.navigation.nextEl = nextRef.current;
                swiperRef.current.navigation.init();
                swiperRef.current.navigation.update();
            });
        }
    }, []);

    // Videos are preloaded upfront, so just kick playback the instant a
    // slide becomes active instead of waiting on the autoPlay attribute.
    useEffect(() => {
        Object.entries(videoRefs.current).forEach(([idx, el]) => {
            if (!el) return;
            if (Number(idx) === activeIndex) {
                el.currentTime = 0;
                el.play().catch(() => {});
            } else {
                el.pause();
            }
        });
    }, [activeIndex]);

    const total = trim?.length || 0;
    const pad = (n) => String(n).padStart(2, "0");

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            {/* Swiper */}
            <Box sx={{ width: "100%", height: { xs: "85vh", sm: "85vh", md: "90vh" } }}>
                <Swiper
                    loop={true}
                    modules={[Navigation]}
                    spaceBetween={20}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                    slidesPerView={1}
                    navigation={{
                        prevEl: prevRef.current,
                        nextEl: nextRef.current,
                    }}
                    style={{ width: "100%", height: "100%" }}
                >
                    {trim?.map((item, index) => (
                        <SwiperSlide key={index}>
                            <Box position="relative" width="100%" height="100%" sx={{ backgroundColor: "#000" }}>
                                {!loadedSlides[index] && (
                                    <Skeleton
                                        animation="wave"
                                        variant="rectangular"
                                        width="100%"
                                        height="100%"
                                        sx={{ position: "absolute", bgcolor: "grey.900" }}
                                    />
                                )}

                                {item.cover.includes(".mp4") ? (
                                    <Box
                                        component="video"
                                        ref={(el) => { videoRefs.current[index] = el; }}
                                        src={isMobile && item?.mobileCover ? item.mobileCover : item.cover}
                                        autoPlay={activeIndex === index}
                                        loop
                                        preload="auto"
                                        fetchpriority={index === 0 ? "high" : "auto"}
                                        muted
                                        playsInline
                                        onLoadedData={() => markLoaded(index)}
                                        sx={{ position: "absolute", width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" }}
                                    />
                                ) : (
                                    <Box
                                        component="img"
                                        loading="eager"
                                        decoding="async"
                                        fetchpriority={index === 0 ? "high" : "auto"}
                                        src={isMobile && item?.mobileCover ? item.mobileCover : item.cover}
                                        onLoad={() => markLoaded(index)}
                                        alt={item.title || "Banner Image"}
                                        sx={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center",
                                            backgroundColor: "black"
                                        }}
                                    />
                                )}

                                {/* Gradient scrim for text legibility */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        background:
                                            "linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0) 70%), linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0) 55%)",
                                    }}
                                />

                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: { xs: "6%", sm: "6%", md: "5%" },
                                        top: "65%",
                                        transform: "translateY(-50%)",
                                        textAlign: "left",
                                        zIndex: 2,
                                        color: "#FFF",
                                        width: "100%",
                                        maxWidth: { xs: "80%", sm: "420px", md: "520px" },
                                    }}
                                >
                                    {item.title &&
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1.5,
                                                lineHeight: 1.05,
                                                textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                                                fontSize: {
                                                    xs: "1.7rem",
                                                    sm: "2.2rem",
                                                    md: "2.7rem",
                                                    lg: "3.2rem",
                                                },
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                    }

                                    {item.subtitle &&
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: { xs: 3, sm: 4 } }}>
                                            <Box sx={{ width: "2px", height: "20px", backgroundColor: "#FFF", flexShrink: 0 }} />
                                            <Typography
                                                component="p"
                                                sx={{
                                                    textShadow: "0 1px 6px rgba(0,0,0,0.5)",
                                                    fontSize: {
                                                        xs: "0.75rem",
                                                        sm: "0.9rem",
                                                        md: "1rem",
                                                    },
                                                }}
                                            >
                                                {item.subtitle}
                                            </Typography>
                                        </Box>
                                    }

                                    <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 } }}>
                                        {item.link !== "" &&
                                            <Button
                                                variant="outlined"
                                                sx={{
                                                    px: { xs: 2.5, sm: 3, md: 4 },
                                                    py: { xs: 1, sm: 1.25, md: 1.5 },
                                                    borderRadius: "0px",
                                                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    color: "#fff",
                                                    borderColor: "rgba(255,255,255,0.8)",
                                                    "&:hover": {
                                                        borderColor: "#fff",
                                                        backgroundColor: "rgba(255,255,255,0.1)",
                                                    },
                                                }}
                                                onClick={() => navigate(item.link)}
                                            >
                                                Learn More
                                            </Button>
                                        }
                                    </Box>
                                </Box>

                            </Box>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>

            {/* Bottom bar: slide counter, nav arrows and pagination dots */}
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: { xs: "4%", sm: "5%", md: "5%" },
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    px: { xs: 2, sm: 4, md: 6 },
                    zIndex: 10,
                    color: "#FFF",
                }}
            >
                {/* Slide counter */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: "2px", height: "18px", backgroundColor: "#FFF" }} />
                    <Typography sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, fontWeight: 600 }}>
                        {pad(activeIndex + 1)}
                        <Typography component="span" sx={{ opacity: 0.5, ml: 0.5, fontSize: "inherit" }}>
                            / {pad(total)}
                        </Typography>
                    </Typography>
                </Box>

                {/* Arrows + pagination dots */}
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                    <IconButton
                        ref={prevRef}
                        aria-label="Previous Slide"
                        sx={{
                            color: "#FFF",
                            width: { xs: "32px", sm: "40px" },
                            height: { xs: "32px", sm: "40px" },
                            border: "1px solid rgba(255,255,255,0.6)",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", borderColor: "#fff" },
                        }}
                    >
                        <ArrowLeft size={18} />
                    </IconButton>

                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {trim?.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => swiperRef.current?.slideToLoop(i)}
                                sx={{
                                    width: activeIndex === i ? { xs: "18px", sm: "24px" } : "6px",
                                    height: "6px",
                                    borderRadius: "3px",
                                    backgroundColor: activeIndex === i ? "#FFF" : "rgba(255,255,255,0.5)",
                                    cursor: "pointer",
                                    transition: "width 0.25s ease",
                                }}
                            />
                        ))}
                    </Box>

                    <IconButton
                        ref={nextRef}
                        aria-label="Next Slide"
                        sx={{
                            color: "#FFF",
                            width: { xs: "32px", sm: "40px" },
                            height: { xs: "32px", sm: "40px" },
                            border: "1px solid rgba(255,255,255,0.6)",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", borderColor: "#fff" },
                        }}
                    >
                        <ArrowRight size={18} />
                    </IconButton>
                </Box>

                <Box />
            </Box>
        </Box>
    );
}









